import { Check, CheckCheck, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useOrgContext } from "@/hooks/use-org-context";
import { type ActivityCard, useActivityFeed } from "@/lib/api/activity";
import { useBulkRunDecision } from "@/lib/api/connections";
import type { BrowserHandoff, NotificationItem } from "@/lib/api/notifications";
import { useMarkNotificationsRead } from "@/lib/api/notifications";
import { useAuthState } from "@/lib/auth-state";

export interface TimelineItem {
	id: string;
	at: number;
	title: string;
	body?: string | null;
	unread?: boolean;
	href?: string | null;
	/** The specific run this item represents (for peek pane deep-link). */
	runId?: number | null;
	notification?: NotificationItem;
	/** Approval run id when this is an action_approval_needed card. */
	approvalRunId?: number | null;
	/** Kind of pending interaction behind the card ('approval' today). */
	interactionType?: string | null;
	/** Live interaction state from its authoritative per-type source. */
	interactionStatus?: string | null;
	/** Server policy: this interaction can be completed inline from the feed. */
	interactionInline?: boolean;
	/** Named outcomes for a one-click decision, and the field they answer. */
	interactionChoiceField?: string | null;
	interactionChoices?: Array<{ value: string; label: string }> | null;
	notificationId?: number | null;
	browserUrl?: string | null;
	browserHandoff?: BrowserHandoff | null;
}

/** Reading a draft is separate from dismissing it. Keep waiting, preparing,
 * and successfully prepared handoffs until Done; exclude read expired drafts. */
function cardNeedsAttention(card: {
	unread?: boolean;
	status?: string | null;
	interaction_type?: string | null;
	interaction_status?: string | null;
	browser_handoff?: BrowserHandoff | null;
}): boolean {
	if (card.interaction_type === "authorization")
		return card.interaction_status === "pending";
	return (
		Boolean(card.unread) ||
		card.interaction_status === "pending" ||
		card.browser_handoff?.state === "ready" ||
		card.browser_handoff?.state === "preparing" ||
		card.browser_handoff?.state === "completed"
	);
}

export function activityCardToTimelineItem(c: ActivityCard): TimelineItem {
	const isApproval = c.status === "action_approval_needed";
	return {
		id: c.id,
		at: new Date(c.at).getTime(),
		title: c.title,
		body: c.body,
		unread: c.unread,
		href: c.href,
		runId: c.run_id ?? null,
		approvalRunId: isApproval ? (c.run_id ?? null) : null,
		interactionType: c.interaction_type ?? null,
		interactionStatus: c.interaction_status ?? null,
		interactionInline: isApproval ? (c.interaction_inline ?? false) : false,
		interactionChoiceField: isApproval
			? (c.interaction_choice_field ?? null)
			: null,
		interactionChoices: isApproval ? (c.interaction_choices ?? null) : null,
		notificationId: c.notification_id ?? null,
		browserUrl: c.browser_url ?? null,
		browserHandoff: c.browser_handoff ?? null,
		// Synthetic NotificationItem for approval actions / mark-read
		notification:
			c.notification_id != null
				? ({
						id: c.notification_id,
						type: c.status ?? "generic",
						title: c.title,
						body: c.body,
						resource_url: c.href,
						browser_url: c.browser_url ?? null,
						browser_handoff: c.browser_handoff ?? null,
						resource_type: c.run_id != null ? "run" : null,
						resource_id: c.run_id != null ? String(c.run_id) : null,
						is_read: !c.unread,
						created_at: c.at,
						organization_id: "",
						user_id: "",
						platform: c.platform ?? null,
						connection_id: c.connection_id ?? null,
						connection_name: c.connection_name ?? null,
						feed_id: c.feed_id ?? null,
						feed_key: c.feed_key ?? null,
						feed_name: c.feed_name ?? null,
						automation_id: c.automation_id ?? null,
						automation_name: c.automation_name ?? null,
						agent_id: c.agent_id ?? null,
						agent_name: c.agent_name ?? null,
						client_id: c.client_id ?? null,
						client_name: c.client_name ?? null,
						device_worker_id: c.device_worker_id ?? null,
						device_label: c.device_label ?? null,
						device_platform: c.device_platform ?? null,
					} satisfies NotificationItem)
				: undefined,
	};
}

/** Shared attention-inbox state: the unread/pending items plus the bulk
 *  read/approve/reject actions over exactly what is visible. The Activity
 *  stream places the items inline; its Notifications view lists only them. */
export function useAttentionInbox(owner: string | null) {
	const { session } = useAuthState();
	const { orgContext } = useOrgContext();
	const markManyRead = useMarkNotificationsRead(orgContext);
	const bulkDecision = useBulkRunDecision();

	const {
		data: cards = [],
		isLoading,
		isError,
	} = useActivityFeed(session && owner ? owner : null, {
		limit: 50,
		aggregate: true,
		includeNotifications: true,
		includeRuns: false,
		kinds: ["notification"],
	});

	const attentionCards = useMemo(
		() => cards.filter(cardNeedsAttention),
		[cards],
	);

	// Server returns the collapsed feed oldest→newest (that order is what its
	// adjacent-collapse pass needs). The attention list reads top-down, so the
	// display list flips to newest-first; every other consumer of the items
	// (bulk-bar sets, sidebar counts) is order-insensitive.
	const attentionItems = useMemo((): TimelineItem[] => {
		return attentionCards.map(activityCardToTimelineItem).reverse();
	}, [attentionCards]);

	const visibleUnreadIds = [
		...new Set(
			attentionItems.flatMap((item) =>
				item.unread && item.notificationId != null ? [item.notificationId] : [],
			),
		),
	];
	const visibleApprovals = attentionItems.filter(
		(item) =>
			item.interactionType === "approval" &&
			item.interactionStatus === "pending" &&
			item.interactionInline === true &&
			item.approvalRunId != null,
	);
	const visibleApprovalRunIds = [
		...new Set(
			visibleApprovals.flatMap((item) =>
				item.approvalRunId != null ? [item.approvalRunId] : [],
			),
		),
	];

	const markVisibleRead = async () => {
		const result = await markManyRead.mutateAsync(visibleUnreadIds);
		if (result.failed > 0) {
			toast.error(
				`Marked ${result.succeeded} read; ${result.failed} could not be updated.`,
			);
		} else {
			toast.success(
				`Marked ${result.succeeded} notification${result.succeeded === 1 ? "" : "s"} as read.`,
			);
		}
	};

	const decideVisibleApprovals = async (
		decision: "approve" | "reject",
		reason?: string,
	) => {
		const result = await bulkDecision.mutateAsync({
			decision,
			runIds: visibleApprovalRunIds,
			...(reason ? { reason } : {}),
		});
		const failed = new Set(result.failedRunIds);
		const decidedNotificationIds = visibleApprovals.flatMap((item) =>
			item.approvalRunId != null &&
			!failed.has(item.approvalRunId) &&
			item.notificationId != null
				? [item.notificationId]
				: [],
		);
		if (decidedNotificationIds.length > 0) {
			await markManyRead.mutateAsync(decidedNotificationIds);
		}
		if (result.failed > 0) {
			toast.error(
				`${result.succeeded} ${decision === "approve" ? "approved" : "rejected"}; ${result.failed} failed.`,
			);
		} else {
			toast.success(
				`${result.succeeded} approval${result.succeeded === 1 ? "" : "s"} ${decision === "approve" ? "approved" : "rejected"}.`,
			);
		}
	};

	const bulkPending = bulkDecision.isPending || markManyRead.isPending;

	return {
		session,
		isLoading,
		isError,
		notificationCards: cards,
		attentionCards,
		attentionItems,
		visibleUnreadIds,
		visibleApprovalRunIds,
		markVisibleRead,
		decideVisibleApprovals,
		markManyRead,
		bulkPending,
	};
}

/** Bulk actions for the visible attention items — rendered beside an
 *  attention list. Hidden when there is nothing to do. */
export function AttentionBulkBar({
	owner,
	showMarkRead = true,
}: {
	owner: string;
	/** Off where each section already offers its own read-all (Home). */
	showMarkRead?: boolean;
}) {
	const {
		session,
		isLoading,
		attentionItems,
		visibleUnreadIds,
		visibleApprovalRunIds,
		markVisibleRead,
		decideVisibleApprovals,
		bulkPending,
	} = useAttentionInbox(owner);
	const [showBulkReject, setShowBulkReject] = useState(false);
	const [bulkRejectReason, setBulkRejectReason] = useState("");

	if (
		!session ||
		isLoading ||
		attentionItems.length === 0 ||
		((!showMarkRead || visibleUnreadIds.length === 0) &&
			visibleApprovalRunIds.length === 0)
	) {
		return null;
	}

	return (
		<div className="flex flex-wrap items-center justify-end gap-2">
			{showMarkRead && visibleUnreadIds.length > 0 ? (
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="h-7 rounded-md bg-muted text-xs"
					disabled={bulkPending}
					onClick={() => void markVisibleRead()}
				>
					<CheckCheck className="h-3.5 w-3.5" />
					Mark {visibleUnreadIds.length} as read
				</Button>
			) : null}
			{visibleApprovalRunIds.length > 0 ? (
				<>
					<Button
						type="button"
						size="sm"
						className="h-7 rounded-md text-xs"
						disabled={bulkPending}
						onClick={() => void decideVisibleApprovals("approve")}
					>
						<Check className="h-3.5 w-3.5" />
						Approve {visibleApprovalRunIds.length}
					</Button>
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="h-7 rounded-md bg-muted text-xs"
						disabled={bulkPending}
						onClick={() => setShowBulkReject(true)}
					>
						<X className="h-3.5 w-3.5" />
						Reject {visibleApprovalRunIds.length}
					</Button>
				</>
			) : null}

			{showBulkReject ? (
				<div className="flex w-full flex-wrap items-center gap-2 rounded-md bg-muted/40 p-2">
					<input
						type="text"
						aria-label="Reject reason"
						placeholder="Reason (optional)"
						value={bulkRejectReason}
						onChange={(event) => setBulkRejectReason(event.target.value)}
						className="h-7 min-w-48 flex-1 rounded-md border bg-background px-2 text-xs"
					/>
					<Button
						type="button"
						variant="destructive"
						size="sm"
						className="h-7 rounded-md text-xs"
						disabled={bulkPending}
						onClick={() => {
							void decideVisibleApprovals(
								"reject",
								bulkRejectReason.trim() || undefined,
							);
							setShowBulkReject(false);
							setBulkRejectReason("");
						}}
					>
						Confirm reject {visibleApprovalRunIds.length}
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-7 text-xs"
						onClick={() => setShowBulkReject(false)}
					>
						Cancel
					</Button>
				</div>
			) : null}
		</div>
	);
}
